import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getClub, getClubMemberships } from '../utils/clubs';
import { getUserProfile } from '../utils/database';
import { getAgeGroup, AGE_GROUP_ORDER, AVAILABLE_AGE_GROUPS } from '../utils/ageGroups';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import { seedDemoData } from '../utils/seedDemoData';
import '../styles/Dashboard.css';

function ClubDashboard() {
  const { userId, clubId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [club, setClub] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (cancelled) return;
      if (!currentUser) {
        navigate('/signin', { replace: true });
        return;
      }
      const uid = currentUser.uid;
      const userData = await getUserProfile(uid);
      if (cancelled) return;
      setUser(userData);

      try {
        const clubData = await getClub(clubId);
        if (cancelled) return;
        if (!clubData || clubData.ownerId !== uid) {
          navigate(`/${uid}/dashboard`, { replace: true });
          return;
        }
        setClub(clubData);
      } catch (err) {
        console.error('[ClubDashboard] Club load error:', err);
        if (!cancelled) navigate(`/${uid}/dashboard`, { replace: true });
        return;
      } finally {
        if (!cancelled) setLoading(false);
      }

      try {
        const mems = await getClubMemberships(clubId);
        if (cancelled) return;
        const profiles = await Promise.all(
          mems.map((m) => getUserProfile(m.userId).catch(() => null))
        );
        if (cancelled) return;
        setPlayers(
          mems.map((m, i) => ({
            ...m,
            profile: profiles[i],
            ageGroup: getAgeGroup(profiles[i]?.birthdate),
          }))
        );
      } catch (err) {
        console.error('[ClubDashboard] Memberships load error (non-critical):', err);
      }
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [clubId, navigate]);

  const teams = club?.teams || [];
  const hasTeams = teams.length > 0;
  const needsSetup = !hasTeams || club?.markupPerMonth == null;

  const rosterGrouped = hasTeams
    ? AVAILABLE_AGE_GROUPS.reduce((acc, ag) => {
        const agTeams = teams.filter((t) => t.ageGroup === ag);
        agTeams.forEach((t) => {
          const teamPlayers = players.filter((p) => p.ageGroup === ag);
          if (teamPlayers.length > 0 || agTeams.length > 0) {
            acc.push({ ...t, players: teamPlayers });
          }
        });
        return acc;
      }, [])
    : AGE_GROUP_ORDER.map((ag) => ({
        id: ag,
        ageGroup: ag,
        label: null,
        players: players.filter((p) => p.ageGroup === ag),
      }));

  const clubName = club?.name || '';

  return (
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />
      <DashboardSidebar userId={userId} user={user} />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            {clubName ? (
              <h1 className="dashboard-content-loaded">{clubName}</h1>
            ) : (
              <div className="dashboard-skeleton-title" />
            )}
          </div>
        </div>

        {/* Nav buttons */}
        <div className="club-dashboard-nav">
          <Link to={`/${userId}/club/${clubId}`} className="club-dashboard-nav-btn active">
            Teams
          </Link>
          <Link to={`/${userId}/club/${clubId}/reports`} className="club-dashboard-nav-btn">
            Reports
          </Link>
          <Link to={`/${userId}/club/${clubId}/manage`} className="club-dashboard-nav-btn club-dashboard-nav-right">
            Club Settings
          </Link>
        </div>

        {/* Setup nudge */}
        {club && needsSetup && (
          <div className="dashboard-setup-banner">
            <div className="dashboard-setup-banner-content">
              <div className="dashboard-setup-banner-text">
                <strong>Finish setting up {clubName}</strong>
                <p>Add teams, set pricing, and upload your logo so athletes can find you.</p>
              </div>
              <div className="dashboard-setup-banner-actions">
                <button
                  className="c-ripple dashboard-setup-banner-btn"
                  onClick={() => navigate(`/${userId}/club/${clubId}/manage`)}
                >
                  Complete Setup
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="settings-content">

          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="dashboard-card club-skeleton-card">
                  <div className="club-skeleton-stat" />
                  <div className="club-skeleton-row" />
                  <div className="club-skeleton-row" />
                  <div className="club-skeleton-row" />
                </div>
              ))}
            </>
          ) : (
            <>
              {rosterGrouped.map((group) => {
                if (group.players.length === 0 && !hasTeams) return null;
                const title = group.label
                  ? `${group.ageGroup} — ${group.label}`
                  : group.ageGroup;
                return (
                  <div key={group.id} className="dashboard-card club-roster-card dashboard-content-loaded">
                    <h3 className="settings-section-header">{title}</h3>
                    {group.players.length > 0 ? (
                      <div className="club-roster-list">
                        {group.players.map((p) => (
                          <div key={p.id} className="club-roster-row">
                            <span>{p.profile?.firstName} {p.profile?.lastName}</span>
                            <span className="club-roster-age">
                              {p.profile?.birthdate ? new Date().getFullYear() - new Date(p.profile.birthdate).getFullYear() : '—'} yrs
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="club-empty-text">No players assigned</p>
                    )}
                  </div>
                );
              })}

              {players.length === 0 && !loading && (
                <div className="dashboard-card dashboard-content-loaded">
                  <p className="club-empty-text">No players yet. Share your landing page to invite athletes.</p>
                </div>
              )}
            </>
          )}

          {/* TEMP: Seed demo data */}
          <div className="dashboard-card">
            <button
              type="button"
              className="club-dashboard-nav-btn"
              style={{ width: '100%', padding: '12px', background: 'rgba(255,0,0,0.15)', borderColor: '#FF0000', color: '#fff' }}
              onClick={async () => {
                setSeeding(true);
                try {
                  await seedDemoData();
                  window.location.reload();
                } catch (err) {
                  console.error('Seed failed:', err);
                  alert('Seed failed: ' + err.message);
                } finally {
                  setSeeding(false);
                }
              }}
              disabled={seeding}
            >
              {seeding ? 'Seeding demo data...' : 'Load Demo Data'}
            </button>
          </div>

        </div>
      </div>
      <BottomNav userId={userId} user={user} />
    </div>
  );
}

export default ClubDashboard;
